import axios, { AxiosError, AxiosRequestConfig } from 'axios';
import { authStorage } from '../auth/auth-storage';
import { clearSession, setSession } from '../store/authSlice';
import { store } from '../store/store';
import { ApiErrorPayload, ApiResponse, AuthSession } from '../types/api';

const baseURL = process.env.REACT_APP_API_URL || 'http://localhost:3000/api';
const apiClient = axios.create({
  baseURL,
  headers: { 'Content-Type': 'application/json' },
});

type RetriableRequestConfig = AxiosRequestConfig & { _authRetry?: boolean };
let refreshPromise: Promise<AuthSession> | null = null;

function isAuthenticationEndpoint(url?: string): boolean {
  return Boolean(url && /\/auth\/(login|refresh|logout)(?:$|\?)/.test(url));
}

function refreshAccessToken(): Promise<AuthSession> {
  if (refreshPromise) return refreshPromise;

  const current = authStorage.get();
  if (!current?.refreshToken) {
    return Promise.reject(new Error('No refresh token available'));
  }

  refreshPromise = axios
    .post<ApiResponse<{ accessToken: string; refreshToken: string }>>(
      `${baseURL}/auth/refresh`,
      { refreshToken: current.refreshToken },
      { headers: { 'Content-Type': 'application/json' } },
    )
    .then((response) => {
      const session: AuthSession = {
        ...current,
        ...response.data.data,
      };
      authStorage.save(session);
      store.dispatch(setSession(session));
      return session;
    })
    .catch((error) => {
      authStorage.clear();
      store.dispatch(clearSession());
      throw error;
    })
    .finally(() => {
      refreshPromise = null;
    });

  return refreshPromise;
}

apiClient.interceptors.request.use((config) => {
  const token = authStorage.getAccessToken();
  if (token) config.headers.Authorization = `Bearer ${token}`;
  return config;
});

export class ApiRequestError extends Error {
  readonly statusCode?: number;
  readonly details?: ApiErrorPayload['details'];

  constructor(payload: ApiErrorPayload, statusCode?: number) {
    const message = Array.isArray(payload.message)
      ? payload.message.join(', ')
      : payload.message;
    super(message);
    this.name = 'ApiRequestError';
    this.statusCode = statusCode ?? payload.statusCode;
    this.details = payload.details;
  }
}

apiClient.interceptors.response.use(
  (response) => response,
  (error: AxiosError<ApiErrorPayload>) => {
    const config = error.config as RetriableRequestConfig | undefined;
    if (
      error.response?.status === 401 &&
      config &&
      !config._authRetry &&
      !isAuthenticationEndpoint(config.url) &&
      authStorage.getRefreshToken()
    ) {
      config._authRetry = true;
      return refreshAccessToken().then(() => apiClient.request(config));
    }

    const payload = error.response?.data;
    if (payload?.message) {
      return Promise.reject(new ApiRequestError(payload, error.response?.status));
    }
    return Promise.reject(error);
  },
);

export const api = {
  get: <T>(url: string, config?: AxiosRequestConfig) =>
    apiClient.get<T>(url, config),
  post: <T>(url: string, body?: unknown, config?: AxiosRequestConfig) =>
    apiClient.post<T>(url, body, config),
  patch: <T>(url: string, body?: unknown, config?: AxiosRequestConfig) =>
    apiClient.patch<T>(url, body, config),
  put: <T>(url: string, body?: unknown, config?: AxiosRequestConfig) =>
    apiClient.put<T>(url, body, config),
};

export default apiClient;
